import { createContext, useContext, useId } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import CloseIcon from '@mui/icons-material/Close';

interface ModalContextValue {
  titleId: string;
  descriptionId: string;
  onClose: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext(component: string): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) throw new Error(`${component} deve essere usato dentro <Modal>.`);
  return context;
}

export interface ModalProps extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Blocks backdrop/Escape dismissal – use for forms with unsaved input. */
  disableClose?: boolean;
  /** Renders the dialog as a `<form>`, so Enter submits. */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
}

/**
 * Compound modal: `Modal.Header`, `Modal.Body`, `Modal.Footer`.
 *
 * The compound shape (rather than `title`/`content`/`actions` props) is what
 * lets a caller drop a status badge into the header or a validation summary
 * above the footer without the component growing a prop for each case. The
 * shared context wires `aria-labelledby`/`aria-describedby` automatically, so
 * accessibility does not depend on the caller remembering to pass IDs.
 */
export function Modal({
  open,
  onClose,
  maxWidth = 'sm',
  disableClose = false,
  onSubmit,
  children,
}: ModalProps): JSX.Element {
  const { isDesktop } = useBreakpoint();
  const baseId = useId();

  const context: ModalContextValue = {
    titleId: `${baseId}-title`,
    descriptionId: `${baseId}-description`,
    onClose,
  };

  return (
    <ModalContext.Provider value={context}>
      <Dialog
        open={open}
        onClose={disableClose ? undefined : onClose}
        maxWidth={maxWidth}
        fullWidth
        // Full-screen on phones: a centred dialog with a keyboard open leaves
        // almost no room for the form.
        fullScreen={!isDesktop}
        aria-labelledby={context.titleId}
        aria-describedby={context.descriptionId}
        // MUI restores focus to the trigger on close; keeping the mount fresh
        // avoids stale form state when the same modal is reopened.
        keepMounted={false}
        PaperProps={onSubmit ? { component: 'form', onSubmit } : undefined}
      >
        {children}
      </Dialog>
    </ModalContext.Provider>
  );
}

export interface ModalHeaderProps extends PropsWithChildren {
  title: string;
  description?: string;
  /** Badges or metadata rendered next to the title. */
  adornment?: ReactNode;
  hideCloseButton?: boolean;
}

function ModalHeader({ title, description, adornment, hideCloseButton }: ModalHeaderProps): JSX.Element {
  const { titleId, descriptionId, onClose } = useModalContext('Modal.Header');

  return (
    <>
      <DialogTitle id={titleId} sx={{ pr: 12 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{title}</span>
          {adornment}
        </Stack>
        {description && (
          <Typography id={descriptionId} variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
            {description}
          </Typography>
        )}
        {!hideCloseButton && (
          <IconButton
            onClick={onClose}
            aria-label="Chiudi finestra"
            sx={{ position: 'absolute', top: 12, right: 12 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
    </>
  );
}

function ModalBody({ children }: PropsWithChildren): JSX.Element {
  return <DialogContent sx={{ pt: 5 }}>{children}</DialogContent>;
}

function ModalFooter({ children }: PropsWithChildren): JSX.Element {
  return (
    <>
      <Divider />
      <DialogActions sx={{ px: 6, py: 4, gap: 2 }}>{children}</DialogActions>
    </>
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
